import React, { useState, useEffect } from "react";
import videoService from "../services/videoService";

// Composant pour suggérer automatiquement 3 vidéos Pixabay pertinentes
// Composant pour suggérer automatiquement 3 vidéos Pixabay pertinentes
function PixabaySuggestions({ theme, onSelect }) {
  const [videos, setVideos] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    videoService
      .searchPixabayVideos(theme, 3)
      .then((vs) => {
        if (mounted) {
          setVideos(vs.slice(0, 3));
          if (onSelect && vs.length >= 3)
            onSelect(vs.slice(0, 3).map((v) => v.url));
        }
        if (mounted) setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, [theme, onSelect]);
  if (loading)
    return (
      <div className="text-gray-400">Chargement des suggestions vidéo…</div>
    );
  if (!videos.length)
    return (
      <div className="text-gray-400">Aucune vidéo pertinente trouvée.</div>
    );
  return (
    <div className="mt-4">
      <div className="font-semibold mb-2">Vidéos suggérées :</div>
      <div className="grid grid-cols-3 gap-2">
        {videos.map((vid, idx) => (
          <video
            key={vid.url}
            src={vid.preview}
            controls
            className="w-full h-32 object-cover rounded"
          >
            <track kind="captions" />
          </video>
        ))}
      </div>
    </div>
  );
}

function Monia() {
  // --- États principaux ---
  const [prompt, setPrompt] = useState("");
  const [script, setScript] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("");
  const [error, setError] = useState("");
  const [step, setStep] = useState("prompt");
  const [tags, setTags] = useState([]);
  const [subject, setSubject] = useState("");
  const [tiktokTitle, setTiktokTitle] = useState("");
  const [tiktokDescription, setTiktokDescription] = useState("");
  const [generationSource, setGenerationSource] = useState("");
  const [generationFallbackReason, setGenerationFallbackReason] = useState("");
  const [copiedTag, setCopiedTag] = useState(null);
  const [copiedDesc, setCopiedDesc] = useState(false);
  const [pixabayVideos, setPixabayVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [description, setDescription] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [copiedHistory, setCopiedHistory] = useState({});
  const [ttsLoading, setTtsLoading] = useState(false);

  // --- Handlers et helpers ---
  const generateScriptFromPrompt = async (inputPrompt) => {
    setIsLoading(true);
    setLoadingLabel("Génération du script...");
    setError("");

    try {
      const {
        script: newScript,
        rewrittenText,
        tags,
        subject,
        viralTitle,
        tiktokDescription,
        aiError,
        source,
      } = await videoService.generateScript(inputPrompt);
      let nextText = rewrittenText || newScript || "";
      setScript(nextText);
      setTags(tags || []);
      setSubject(subject || "");
      setTiktokTitle(viralTitle || subject || "Titre TikTok");
      setTiktokDescription(
        tiktokDescription ||
          (nextText ? `Resume TikTok : ${nextText.slice(0, 140)}...` : ""),
      );
      setGenerationSource(source || "");
      setGenerationFallbackReason(aiError || "");

      setStep("script");
      return true;
    } catch (err) {
      setError(
        "Erreur lors de la génération du script.\n" + (err?.message || ""),
      );
      return false;
    } finally {
      setLoadingLabel("");
      setIsLoading(false);
    }
  };

  const handlePromptSubmit = async (e) => {
    e.preventDefault();
    await generateScriptFromPrompt(prompt);
  };

  const handleRedoRequest = async () => {
    setPrompt("");
    setScript("");
    setError("");
    setStep("prompt");
    setTags([]);
    setSubject("");
    setTiktokTitle("");
    setTiktokDescription("");
    setGenerationSource("");
    setGenerationFallbackReason("");
    setCopiedTag(null);
    setCopiedDesc(false);
    setPixabayVideos([]);
    setSelectedVideos([]);
    setVideoUrl(null);
    setDescription("");
    setFeedback({});
    setCopiedHistory({});
    setLoadingLabel("");
    setIsLoading(false);
    setTtsLoading(false);

    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", "/monia");
    }
  };

  const handleChange = (e) => setPrompt(e.target.value);

  const estimateDuration = (text) => {
    // Estimation naïve : 3 mots/seconde
    if (!text) return 0;
    const words = text.split(/\s+/).length;
    return Math.round(words / 3);
  };

  const getCenteredOverlayText = (text) => {
    const clean = (text || "").replace(/\s+/g, " ").trim();
    if (!clean) return "Ajoute ton texte pour l'aperçu TikTok";
    return clean.length > 220 ? `${clean.slice(0, 220)}...` : clean;
  };

  const handleTTS = () => {
    if (!script) return;
    if (typeof window === "undefined" || !window.speechSynthesis) {
      setError("Synthèse vocale non disponible sur ce navigateur.");
      return;
    }

    setTtsLoading(true);
    setError("");

    window.speechSynthesis.cancel();
    const utterance = new window.SpeechSynthesisUtterance(script);
    utterance.lang = "fr-FR";
    utterance.rate = 0.97;
    utterance.pitch = 1;
    utterance.volume = 1;

    const voices = window.speechSynthesis.getVoices();
    const frenchVoice = voices.find((voice) =>
      voice.lang?.toLowerCase().startsWith("fr"),
    );
    if (frenchVoice) {
      utterance.voice = frenchVoice;
    }

    utterance.onend = () => setTtsLoading(false);
    utterance.onerror = () => {
      setTtsLoading(false);
      setError("Erreur de lecture TTS. Réessaie avec un texte plus court.");
    };

    window.speechSynthesis.speak(utterance);
  };

  const handleScriptConfirm = async () => {
    setIsLoading(true);
    setLoadingLabel("Monia cherche les meilleures vidéos...");
    setError("");
    setPixabayVideos([]);
    setSelectedVideos([]);
    try {
      const videos = await videoService.searchPixabayVideos(
        subject || prompt,
        6,
      );
      setPixabayVideos(videos);
      setSelectedVideos(videos.slice(0, 3).map((v) => v.url));
      setStep("select");
    } catch (err) {
      setError(
        "Erreur lors de la recherche de vidéos Pixabay.\n" +
          (err?.message || ""),
      );
    } finally {
      setLoadingLabel("");
      setIsLoading(false);
    }
  };

  const handleVideoSelect = (url) => {
    setSelectedVideos((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url],
    );
  };

  const handleGenerateVideo = async () => {
    if (selectedVideos.length === 0) return;
    setIsLoading(true);
    setLoadingLabel("Monia assemble la vidéo finale...");
    setError("");
    setVideoUrl(null);
    setDescription("");
    setTags([]);
    try {
      const { videoUrl, title, description, tags } =
        await videoService.generateVideoFromSelection(
          script,
          selectedVideos,
          subject,
        );
      setVideoUrl(videoUrl);
      if (title) {
        setTiktokTitle(title);
      }
      setDescription(description);
      setTags(tags);
      setStep("result");
      fetchHistory();
    } catch (err) {
      setError(
        "Erreur lors de la génération de la vidéo finale.\n" +
          (err?.message || ""),
      );
    } finally {
      setLoadingLabel("");
      setIsLoading(false);
    }
  };

  const handleFeedback = (videoUrl, type) => {
    setFeedback((prev) => ({ ...prev, [videoUrl]: type }));
  };

  const handleCopyDesc = (id, desc) => {
    setCopiedHistory((prev) => ({ ...prev, [id + "-desc"]: true }));
    navigator.clipboard.writeText(desc);
    setTimeout(
      () => setCopiedHistory((prev) => ({ ...prev, [id + "-desc"]: false })),
      1200,
    );
  };

  const handleCopyTag = (id, tag) => {
    setCopiedHistory((prev) => ({ ...prev, [id + "-tag-" + tag]: true }));
    navigator.clipboard.writeText(tag);
    setTimeout(
      () =>
        setCopiedHistory((prev) => ({ ...prev, [id + "-tag-" + tag]: false })),
      1200,
    );
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      // À adapter selon ton backend
      const hist = await videoService.getHistory?.();
      setHistory(hist || []);
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  return (
    <div className="max-w-2xl mx-auto py-10 px-4">
      <h1 className="text-2xl font-bold mb-6">Générer une vidéo avec Monia</h1>

      {isLoading && loadingLabel && (
        <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-rose-500" />
            <span>{loadingLabel}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-black">
          {error}
        </div>
      )}

      {/* Étape 1 : prompt */}
      {step === "prompt" && (
        <form onSubmit={handlePromptSubmit} className="mb-8">
          <label htmlFor="prompt" className="block mb-2 font-medium">
            Colle ici ton texte source pour la vidéo :
          </label>
          <textarea
            id="prompt"
            value={prompt}
            onChange={handleChange}
            className="w-full rounded-lg border border-slate-300 bg-white p-3 text-black focus:outline-none focus:ring-2 focus:ring-rose-400 dark:border-slate-300 dark:bg-white dark:text-black"
            rows={4}
            placeholder="Colle ici le texte que tu as trouvé sur Internet..."
          />
          <button
            type="submit"
            className="mt-4 px-6 py-2 rounded-full bg-rose-500 text-white font-semibold hover:bg-rose-600 transition-colors"
            disabled={isLoading || !prompt}
          >
            {isLoading ? "Analyse du texte..." : "Analyser le texte"}
          </button>
        </form>
      )}

      {/* Étape 2 : affichage du script généré */}
      {step === "script" && (
        <div className="space-y-4 mb-8">
          <div className="mb-2 flex flex-wrap gap-2">
            <button
              className="px-4 py-2 bg-rose-600 text-white rounded hover:bg-rose-700 font-semibold"
              onClick={async () => {
                setLoadingLabel("Nouvelle analyse avec l'IA...");
                await generateScriptFromPrompt(prompt);
              }}
              disabled={isLoading}
            >
              {isLoading ? "Analyse..." : "Regénérer titre et description"}
            </button>
            <button
              className="px-4 py-2 bg-slate-700 text-white rounded hover:bg-slate-800 font-semibold"
              onClick={handleRedoRequest}
              disabled={isLoading || !prompt}
            >
              Refaire une demande
            </button>
          </div>
          {/* Estimation de durée et contrôle */}
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm text-gray-700">
              ⏱️ Durée estimée : <b>{estimateDuration(script)}s</b>
            </span>
            {estimateDuration(script) < 15 && (
              <span className="text-xs text-red-600 font-semibold">
                Le texte est trop court pour TikTok (&lt; 15s). Allonge-le pour
                de meilleurs résultats !
              </span>
            )}
            {estimateDuration(script) >= 15 &&
              estimateDuration(script) < 30 && (
                <span className="text-xs text-yellow-600 font-semibold">
                  Durée correcte, mais tu peux viser 30s+ pour maximiser la
                  rétention.
                </span>
              )}
            {estimateDuration(script) >= 30 && (
              <span className="text-xs text-green-600 font-semibold">
                👍 Parfait pour TikTok !
              </span>
            )}
          </div>
          {/* Titre TikTok viral, copiable */}
          {tiktokTitle && (
            <div className="flex items-center gap-2 mb-2">
              <span
                className="cursor-pointer text-xl font-bold text-rose-700 dark:text-rose-700"
                onClick={() => {
                  navigator.clipboard.writeText(tiktokTitle);
                  setCopiedTag(tiktokTitle);
                  setTimeout(() => setCopiedTag(null), 1200);
                }}
                title="Copier le titre TikTok"
              >
                {tiktokTitle}
              </span>
              <button
                className="px-2 py-1 bg-slate-200 text-xs rounded hover:bg-slate-300"
                onClick={() => {
                  navigator.clipboard.writeText(tiktokTitle);
                  setCopiedTag(tiktokTitle);
                  setTimeout(() => setCopiedTag(null), 1200);
                }}
                type="button"
                title="Copier le titre TikTok"
              >
                {copiedTag === tiktokTitle ? "Copié !" : "Copier le titre"}
              </button>
            </div>
          )}
          {/* Description TikTok (sous le titre) */}
          {tiktokDescription && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg font-semibold text-rose-600 dark:text-rose-600">
                {tiktokDescription}
              </span>
              <button
                className="px-2 py-1 bg-slate-200 text-xs rounded hover:bg-slate-300"
                onClick={() => {
                  navigator.clipboard.writeText(tiktokDescription);
                  setCopiedTag(tiktokDescription);
                  setTimeout(() => setCopiedTag(null), 1200);
                }}
                type="button"
                title="Copier la description TikTok"
              >
                {copiedTag === tiktokDescription
                  ? "Copié !"
                  : "Copier la description"}
              </button>
            </div>
          )}
          {/* Affichage dynamique de la source IA */}
          {generationSource && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-black dark:text-black">
              {generationSource === "openai" && (
                <>
                  🧠 Texte généré par <b>ChatGPT (OpenAI)</b>.
                </>
              )}
              {generationSource === "gemini" && (
                <>
                  🧠 Texte généré par <b>Gemini (Google)</b>.
                </>
              )}
              {generationSource === "claude" && (
                <>
                  🧠 Texte généré par <b>Claude (Anthropic)</b>.
                </>
              )}
              {generationSource === "mistral" && (
                <>
                  🧠 Texte généré par <b>Mistral AI</b>.
                </>
              )}
              {generationSource === "free" && (
                <>
                  Mode gratuit actif.{" "}
                  {generationFallbackReason ||
                    "La reformulation locale a été utilisée."}
                </>
              )}
              {!["openai", "gemini", "claude", "mistral", "free"].includes(
                generationSource,
              ) && (
                <>
                  🧠 Texte généré par <b>{generationSource}</b>.
                </>
              )}
            </div>
          )}
          <div className="rounded bg-gray-100 p-3 dark:bg-gray-800">
            <div className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
              Texte source pour la vidéo
            </div>
            <textarea
              className="min-h-[180px] w-full rounded border border-slate-300 bg-white p-3 text-black dark:border-slate-300 dark:bg-white dark:text-black"
              value={script}
              onChange={(event) => {
                setScript(event.target.value);
                setPrompt(event.target.value);
              }}
            />
            <button
              className="mt-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
              onClick={handleTTS}
              disabled={ttsLoading || !script}
            >
              {ttsLoading ? "Lecture en cours..." : "Écouter la voix-off (TTS)"}
            </button>
          </div>
          {/* Zone de tags cliquables */}
          {tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag}
                  className={`px-2 py-1 bg-rose-100 text-rose-700 rounded-full text-xs flex items-center gap-1 hover:bg-rose-200 transition-colors ${copiedTag === tag ? "ring-2 ring-rose-400" : ""}`}
                  onClick={() => {
                    navigator.clipboard.writeText(tag);
                    setCopiedTag(tag);
                    setTimeout(() => setCopiedTag(null), 1200);
                  }}
                  type="button"
                  title="Copier le tag"
                >
                  #{tag}{" "}
                  {copiedTag === tag && (
                    <span className="ml-1 text-rose-500">✔</span>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="rounded border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800">
            Sujet detecte pour Pixabay :{" "}
            <span className="font-semibold">{subject || "non detecte"}</span>
          </div>
          <button
            className="btn-primary mt-4"
            onClick={handleScriptConfirm}
            disabled={isLoading}
          >
            Trouver des vidéos
          </button>
        </div>
      )}

      {/* Étape 3 : sélection des vidéos Pixabay */}
      {step === "select" && (
        <div className="space-y-4 mb-8">
          <div className="font-bold mb-2">
            Vidéos suggérées (3 sélectionnées par défaut) :
          </div>
          <div className="grid grid-cols-3 gap-4">
            {pixabayVideos.map((vid, idx) => (
              <div
                key={vid.url}
                className={`border-4 rounded-lg p-1 cursor-pointer transition-all duration-200 ${selectedVideos.includes(vid.url) ? "border-rose-500 ring-4 ring-rose-400" : "border-gray-200"}`}
                onClick={() => handleVideoSelect(vid.url)}
                tabIndex={0}
                role="button"
                aria-pressed={selectedVideos.includes(vid.url)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    handleVideoSelect(vid.url);
                }}
              >
                <video
                  src={vid.preview}
                  controls
                  className="w-full h-32 object-cover rounded"
                  muted
                >
                  <track kind="captions" />
                </video>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Le montage final sera sans son.
          </div>
          <button
            className="btn-primary mt-4"
            onClick={handleGenerateVideo}
            disabled={isLoading || selectedVideos.length < 3}
          >
            Générer la vidéo finale
          </button>
        </div>
      )}

      {/* Étape 4 : résultat final */}
      {step === "result" && videoUrl && !isLoading && (
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-4 w-[280px] overflow-hidden rounded-[28px] border-4 border-black bg-black shadow-2xl">
            <div className="relative aspect-[9/16] w-full">
              <video
                src={videoUrl}
                className="absolute inset-0 h-full w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
              >
                <track kind="captions" label="Français" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/20 to-black/55" />
              <div className="relative z-10 flex h-full items-center justify-center px-5 text-center">
                <p className="text-[1.05rem] font-semibold leading-tight text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.75)]">
                  {getCenteredOverlayText(script || tiktokTitle)}
                </p>
              </div>
            </div>
          </div>
          <video
            src={videoUrl}
            controls
            className="w-full max-w-md rounded-lg shadow-lg mb-4"
          >
            <track kind="captions" label="Français" />
          </video>
          <button
            className="mb-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
            onClick={handleTTS}
            disabled={ttsLoading || !script}
          >
            {ttsLoading ? "Lecture en cours..." : "Écouter la voix-off (TTS)"}
          </button>
          <div className="flex gap-4">
            <a
              href={videoUrl}
              download
              className="px-4 py-2 rounded-full bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors"
            >
              Télécharger
            </a>
            <button
              onClick={() => setVideoUrl(null)}
              className="px-4 py-2 rounded-full bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors"
            >
              Supprimer
            </button>
          </div>
          {/* Description conseillée et tags dynamiques */}
          <div className="mb-8 w-full max-w-md mx-auto">
            {/* Feedback utilisateur */}
            <div className="flex gap-4 mt-2 mb-4">
              <button
                className={`px-4 py-2 rounded-full font-semibold transition-colors ${feedback[videoUrl] === "like" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-green-100"}`}
                onClick={() => handleFeedback(videoUrl, "like")}
              >
                👍 Utile
              </button>
              <button
                className={`px-4 py-2 rounded-full font-semibold transition-colors ${feedback[videoUrl] === "dislike" ? "bg-red-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-red-100"}`}
                onClick={() => handleFeedback(videoUrl, "dislike")}
              >
                👎 À améliorer
              </button>
            </div>
            {description && (
              <div className="mb-4">
                <label
                  htmlFor="description"
                  className="block font-semibold mb-1"
                >
                  Description conseillée :
                </label>
                <textarea
                  className="w-full rounded-lg border border-slate-300 bg-gray-50 p-2 text-sm text-black dark:border-slate-300 dark:bg-white dark:text-black"
                  rows={3}
                  value={description}
                  readOnly
                />
                <button
                  className="mt-2 px-3 py-1 rounded bg-slate-200 text-xs hover:bg-slate-300"
                  onClick={() => {
                    navigator.clipboard.writeText(description);
                    setCopiedDesc(true);
                    setTimeout(() => setCopiedDesc(false), 1200);
                  }}
                >
                  {copiedDesc ? "Copié !" : "Copier"}
                </button>
              </div>
            )}
            {tags.length > 0 && (
              <div>
                <label htmlFor="tags-list" className="block font-semibold mb-1">
                  Tags suggérés :
                </label>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <button
                      key={tag}
                      className={`px-2 py-1 bg-rose-100 text-rose-700 rounded-full text-xs flex items-center gap-1 hover:bg-rose-200 transition-colors ${copiedTag === tag ? "ring-2 ring-rose-400" : ""}`}
                      onClick={() => {
                        navigator.clipboard.writeText(tag);
                        setCopiedTag(tag);
                        setTimeout(() => setCopiedTag(null), 1200);
                      }}
                      type="button"
                      title="Copier le tag"
                    >
                      #{tag}{" "}
                      {copiedTag === tag && (
                        <span className="ml-1 text-rose-500">✔</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Historique des vidéos générées */}
      <div className="mt-12">
        <h2 className="text-xl font-bold mb-4">
          Historique des vidéos générées
        </h2>
        {(() => {
          if (loadingHistory) {
            return (
              <div className="text-gray-500">Chargement de l’historique…</div>
            );
          }
          if (history.length === 0) {
            return (
              <div className="text-gray-400">
                Aucune vidéo générée pour l’instant.
              </div>
            );
          }
          return (
            <div className="flex flex-col gap-8">
              {history.map((video) => (
                <div
                  key={video.id}
                  className="border rounded-lg p-4 flex flex-col md:flex-row gap-4 items-center bg-white/80 dark:bg-gray-900/80 shadow"
                >
                  <video
                    src={video.video_url}
                    controls
                    className="w-[180px] h-[320px] rounded-lg shadow-lg object-cover bg-black"
                    style={{ aspectRatio: "9/16", maxHeight: 320 }}
                  >
                    <track kind="captions" label="Français" />
                  </video>
                  <div className="flex-1 w-full">
                    <div className="mb-2 text-xs text-gray-500">
                      {new Date(video.created_at).toLocaleString()}
                    </div>
                    <div className="mb-2">
                      <span className="font-semibold">Prompt :</span>{" "}
                      <span className="text-gray-700">{video.prompt}</span>
                    </div>
                    <div className="mb-2 flex items-center gap-2">
                      <span className="font-semibold">Description :</span>
                      <input
                        id="description"
                        className="w-auto max-w-xs rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 dark:border-white/10 dark:bg-slate-900 dark:text-slate-100"
                        value={video.description}
                        readOnly
                        style={{ minWidth: 80 }}
                      />
                      <button
                        className="px-2 py-1 rounded bg-slate-200 text-xs hover:bg-slate-300"
                        onClick={() =>
                          handleCopyDesc(video.id, video.description)
                        }
                      >
                        {copiedHistory[video.id + "-desc"]
                          ? "Copié !"
                          : "Copier"}
                      </button>
                    </div>
                    {Array.isArray(video.tags) && video.tags.length > 0 && (
                      <div className="mb-2">
                        <span className="font-semibold">Tags :</span>
                        <div
                          id="tags-list"
                          className="flex flex-wrap gap-2 mt-1"
                        >
                          {video.tags.map((tag) => (
                            <button
                              key={tag}
                              className={`px-2 py-1 bg-rose-100 text-rose-700 rounded-full text-xs flex items-center gap-1 hover:bg-rose-200 transition-colors ${copiedHistory[video.id + "-tag-" + tag] ? "ring-2 ring-rose-400" : ""} dark:bg-rose-100 dark:text-rose-700`}
                              onClick={() => handleCopyTag(video.id, tag)}
                              type="button"
                              title="Copier le tag"
                            >
                              #{tag}{" "}
                              {copiedHistory[video.id + "-tag-" + tag] && (
                                <span className="ml-1 text-rose-500">✔</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-3 mt-2">
                      <a
                        href={video.video_url}
                        download
                        className="px-4 py-2 rounded-full bg-green-500 text-white font-semibold hover:bg-green-600 transition-colors"
                      >
                        Télécharger
                      </a>
                      <button
                        className="px-4 py-2 rounded-full bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors"
                        onClick={async () => {
                          if (
                            typeof window !== "undefined" &&
                            window.confirm("Supprimer cette vidéo ?")
                          ) {
                            await videoService.deleteVideo(video.id);
                            fetchHistory();
                          }
                        }}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default Monia;
